/**
 * Formatting utilities for display
 */

/**
 * Format phone number with optional prefix
 * Input: "5551234567" or "+15551234567"
 * Output: "+1-555-123-4567"
 */
export const formatPhone = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `+1-${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1-${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone; // Return original if can't format
};

/**
 * Format file size in bytes to human-readable format
 * Input: 1024
 * Output: "1 KB"
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format ISO timestamp to human-readable date/time in Indian Standard Time (IST)
 * Input: "2026-05-17T14:30:00Z" or "2026-05-17T14:30:00"
 * Output: "17 May 2026, 8:00 PM" (IST)
 */
export const formatDateTime = (isoString: string): string => {
  try {
    if (!isoString) return '';
    // Postgres TIMESTAMP (without timezone) returns strings like "2026-06-05T17:30:00"
    // We must append 'Z' to force UTC parsing if it's missing timezone info
    const validIsoString = isoString.endsWith('Z') || isoString.includes('+') ? isoString : `${isoString}Z`;
    const date = new Date(validIsoString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  } catch {
    return isoString;
  }
};

/**
 * Format ISO timestamp to time only in Indian Standard Time (IST)
 */
export const formatTime = (isoString: string): string => {
  try {
    if (!isoString) return '';
    const validIsoString = isoString.endsWith('Z') || isoString.includes('+') ? isoString : `${isoString}Z`;
    const date = new Date(validIsoString);
    return date.toLocaleString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  } catch {
    return isoString;
  }
};

/**
 * Format ISO timestamp to date only (today/yesterday/date) in Indian Standard Time (IST)
 */
export const formatDate = (isoString: string): string => {
  try {
    if (!isoString) return '';
    const validIsoString = isoString.endsWith('Z') || isoString.includes('+') ? isoString : `${isoString}Z`;
    const date = new Date(validIsoString);
    
    // Convert to IST for comparison
    const istDate = new Date(date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
    const today = new Date();
    const istToday = new Date(today.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
    
    const yesterday = new Date(istToday);
    yesterday.setDate(yesterday.getDate() - 1);

    if (istDate.toDateString() === istToday.toDateString()) {
      return 'Today';
    }
    if (istDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }

    return istDate.toLocaleString('en-IN', {
      month: 'short',
      day: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
  } catch {
    return isoString;
  }
};

/**
 * Get status badge color
 */
export const getStatusColor = (
  status: 'pending' | 'printing' | 'completed' | 'failed' | 'archived'
): string => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'printing':
      return 'bg-blue-100 text-blue-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'archived':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
